import { type WatchSource, type WatchStopHandle, computed, watch } from 'vue';
import { useMenuStore } from './store';
import type { MenuItem } from './types/menuItem';

export const useMenu = () => {
  const store = useMenuStore();

  const add = (...items: MenuItem[]) => {
    items.forEach((item) => {
      item.items?.forEach((child) => add({ ...child, name: `${item.name}.${child.name}` }));

      const index = store.items.findIndex((i) => i.name === item.name);

      if (index > -1) {
        store.items[index] = item;
      } else {
        store.items.push(item);
      }
    });
  };

  const removeMenuItem = (item: MenuItem) => {
    const index = store.items.findIndex((i) => i.name === item.name);

    if (index > -1) {
      store.items.splice(index, 1);
    }
  };

  /**
   * ! Should only be used internally or when tearing down modules.
   */
  const remove = (...items: MenuItem[]) => {
    items.forEach((item) => {
      if (stopHandles[item.name]) {
        stopHandles[item.name]();
      }

      item.items?.forEach((child) => remove({ ...child, name: `${item.name}.${child.name}` }));

      removeMenuItem(item);
    });
  };

  const removeQuietly = (...items: MenuItem[]) => {
    items.forEach((item) => {
      item.items?.forEach((child) =>
        removeQuietly({ ...child, name: `${item.name}.${child.name}` }),
      );

      removeMenuItem(item);
    });
  };

  const stopHandles: Record<string, WatchStopHandle> = {};
  const addWhen = (source: WatchSource<boolean>, ...items: MenuItem[]) => {
    items.forEach((item) => {
      const stopHandle = watch(source, (v) => (v ? add : removeQuietly)(item), {
        immediate: true,
      });

      if (stopHandles[item.name]) {
        stopHandles[item.name]();
      }

      stopHandles[item.name] = stopHandle;
    });
  };

  return {
    items: computed(() => store.nestedItems),
    add,
    remove,
    addWhen,
  };
};
